/**
 * Estilos compartidos por el contenido del chat:
 * cuerpo de lectura, aparato de fuentes, follow-ups, badges de confianza y
 * botones de feedback. Se monta una sola vez a nivel de página.
 *
 * Dirección "Códice nocturno": el texto de Minerva se lee como un pasaje
 * tipografiado en serif editorial (Newsreader); las fuentes son notas al pie.
 */
export default function SharedContentStyles() {
  return (
    <style>{`
      /* ── Cuerpo de lectura (markdown) ── */
      .msg-assistant-body {
        font-family: var(--font-read);
        font-size: 1.02rem;
        line-height: 1.8;
        color: var(--vellum);
      }
      .msg-assistant-body p { margin-bottom: 13px; color: var(--vellum); }
      .msg-assistant-body p:last-child { margin-bottom: 0; }
      .msg-assistant-body h1 {
        font-family: var(--font-display);
        font-size: 1.5rem; font-weight: 600; font-optical-sizing: auto;
        color: var(--vellum);
        margin: 22px 0 10px; line-height: 1.2; letter-spacing: 0.01em;
      }
      .msg-assistant-body h2 {
        font-family: var(--font-display);
        font-size: 1.25rem; font-weight: 600; font-optical-sizing: auto;
        color: var(--vellum);
        margin: 20px 0 8px; line-height: 1.25;
      }
      .msg-assistant-body h3 {
        font-family: var(--font-ui);
        font-size: 0.78rem; font-weight: 600;
        letter-spacing: 0.08em; text-transform: uppercase;
        color: var(--gilt);
        margin: 18px 0 6px;
      }
      .msg-assistant-body ul {
        list-style: none; padding-left: 4px; margin-bottom: 13px;
        display: flex; flex-direction: column; gap: 6px;
      }
      .msg-assistant-body ul li {
        position: relative; padding-left: 20px;
      }
      .msg-assistant-body ul li::before {
        content: '';
        position: absolute; left: 4px; top: 0.72em;
        width: 5px; height: 5px;
        background: var(--gilt); opacity: 0.7;
        transform: rotate(45deg);
      }
      .msg-assistant-body ol {
        list-style: none; counter-reset: item; padding-left: 4px; margin-bottom: 13px;
        display: flex; flex-direction: column; gap: 6px;
      }
      .msg-assistant-body ol li {
        position: relative; padding-left: 26px; counter-increment: item;
      }
      .msg-assistant-body ol li::before {
        content: counter(item);
        position: absolute; left: 0; top: 0.06em;
        font-family: var(--font-mono); font-size: 0.78em; font-weight: 500;
        color: var(--gilt);
      }
      .msg-assistant-body li { color: var(--vellum); line-height: 1.65; }
      .msg-assistant-body strong { color: var(--gilt-bright); font-weight: 600; }
      .msg-assistant-body em { font-style: italic; color: var(--vellum-dim); }
      .msg-assistant-body a {
        color: var(--gilt-bright);
        text-decoration: none;
        border-bottom: 1px solid var(--gilt-line);
        transition: border-color 0.18s ease;
      }
      .msg-assistant-body a:hover { border-bottom-color: var(--gilt); }
      .msg-assistant-body blockquote {
        border-left: 2px solid var(--gilt-line);
        padding: 2px 0 2px 16px; margin: 14px 0;
        font-family: var(--font-display);
        font-style: italic; font-size: 1.08rem;
        color: var(--vellum-dim);
      }
      .msg-assistant-body code {
        background: var(--ink-850);
        border: 1px solid var(--gilt-hair);
        color: var(--gilt-bright);
        padding: 1px 6px; border-radius: 4px;
        font-size: 0.82em; font-family: var(--font-mono);
      }
      .msg-assistant-body pre {
        background: var(--ink-850);
        border: 1px solid var(--gilt-hair);
        border-left: 2px solid var(--gilt-line);
        border-radius: 8px; padding: 14px 16px; overflow-x: auto; margin: 14px 0;
        font-family: var(--font-mono);
        font-size: 0.82em; line-height: 1.6;
      }
      .msg-assistant-body pre code { background: none; border: none; padding: 0; color: var(--vellum); }
      .msg-assistant-body .katex-display {
        margin: 14px 0; padding: 2px 0;
        overflow-x: auto; overflow-y: hidden;
      }

      /* ── Fuentes (aparato de notas al pie) ── */
      .refs-block {
        margin-top: 22px; padding-top: 16px;
        border-top: 1px solid var(--gilt-hair);
      }
      .refs-label {
        font-family: var(--font-ui);
        font-size: 0.68rem; font-weight: 600; letter-spacing: 0.16em;
        text-transform: uppercase; color: var(--gilt);
        margin-bottom: 12px;
      }
      .refs-list { list-style: none; display: flex; flex-direction: column; gap: 12px; }
      .ref-note { display: flex; gap: 12px; align-items: baseline; }
      .ref-index {
        flex-shrink: 0;
        font-family: var(--font-mono);
        font-size: 0.72rem; font-weight: 500;
        color: var(--gilt);
        min-width: 16px; text-align: right;
        line-height: 1.4;
      }
      .ref-body { flex: 1; min-width: 0; }
      .ref-source {
        font-family: var(--font-ui);
        font-size: 0.84rem; font-weight: 500;
        color: var(--vellum);
        word-break: break-word;
      }
      .ref-loc {
        font-family: var(--font-mono);
        font-size: 0.7rem; color: var(--vellum-faint);
        margin-left: 8px;
      }
      .ref-quote {
        font-family: var(--font-read);
        font-size: 0.86rem; font-style: italic;
        color: var(--vellum-dim);
        line-height: 1.5; margin-top: 4px;
        display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
      }

      /* ── Meta: confianza / razonamiento ── */
      .meta-row {
        display: flex; align-items: center; flex-wrap: wrap; gap: 14px;
        margin-top: 16px;
      }
      .meta-badge {
        display: inline-flex; align-items: center; gap: 6px;
        font-family: var(--font-mono);
        font-size: 0.66rem; font-weight: 500; letter-spacing: 0.04em;
        color: var(--vellum-dim);
      }
      .meta-badge i { font-size: 0.85rem; color: var(--gilt); }
      .meta-badge.quality-high i { color: var(--sage); }
      .meta-badge.quality-high { color: var(--sage); }
      .meta-badge.quality-medium i { color: var(--gilt); }
      .meta-badge.quality-low i { color: var(--rose); }
      .meta-badge.quality-low { color: var(--rose); }

      /* ── Follow-ups (preguntas como enlaces sobrios) ── */
      .followups-block { margin-top: 20px; display: flex; flex-direction: column; gap: 9px; }
      .followups-label {
        font-family: var(--font-ui);
        font-size: 0.66rem; font-weight: 600; letter-spacing: 0.14em;
        text-transform: uppercase; color: var(--vellum-faint);
        display: flex; align-items: center; gap: 6px;
      }
      .followups-label i { color: var(--gilt); }
      .followups-grid { display: flex; flex-direction: column; gap: 4px; align-items: flex-start; }
      .followup-chip {
        background: none; border: none;
        color: var(--vellum-dim);
        font-family: var(--font-read);
        font-size: 0.96rem; font-style: italic; line-height: 1.45;
        padding: 3px 0 3px 18px;
        cursor: pointer; text-align: left;
        position: relative;
        transition: color 0.18s ease, padding-left 0.18s cubic-bezier(0.16,1,0.3,1);
      }
      .followup-chip::before {
        content: '\\2192'; /* → */
        font-style: normal;
        position: absolute; left: 0; top: 3px;
        color: var(--gilt); opacity: 0.7;
        font-size: 0.92rem;
        transition: transform 0.18s cubic-bezier(0.16,1,0.3,1);
      }
      .followup-chip:hover:not(:disabled) { color: var(--gilt-bright); padding-left: 24px; }
      .followup-chip:hover:not(:disabled)::before { transform: translateX(3px); }
      .followup-chip:disabled { opacity: 0.45; cursor: default; }

      /* ── Botones de feedback ── */
      .feedback-row { display: flex; align-items: center; gap: 10px; margin-top: 18px; }
      .feedback-prompt {
        font-family: var(--font-mono);
        font-size: 0.64rem; letter-spacing: 0.04em;
        color: var(--vellum-faint);
        margin-right: 2px;
      }
      .feedback-btn {
        display: inline-flex; align-items: center; justify-content: center;
        width: 30px; height: 30px; border-radius: 8px;
        border: 1px solid var(--gilt-hair);
        background: var(--ink-800);
        color: var(--vellum-faint);
        cursor: pointer; font-size: 0.95rem;
        transition: border-color 0.18s ease, color 0.18s ease, transform 0.12s cubic-bezier(0.16,1,0.3,1);
      }
      .feedback-btn:hover:not(:disabled) { border-color: var(--gilt-line); color: var(--vellum); }
      .feedback-btn:active:not(:disabled) { transform: scale(0.92); }
      .feedback-btn:disabled { cursor: default; }
      .feedback-btn.active-up { border-color: var(--sage); color: var(--sage); }
      .feedback-btn.active-down { border-color: var(--rose); color: var(--rose); }
      .feedback-thanks { font-family: var(--font-mono); font-size: 0.66rem; color: var(--sage); }
    `}</style>
  );
}
